import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { VehicleCard } from "@/components/VehicleCard";
import { TrafficLight } from "@/components/TrafficLight";
import { formatRupiah } from "@/lib/format";
import { Calculator, Mountain, GitCompare, Crown, Ticket, ArrowRight, Bookmark, History, MailWarning } from "lucide-react";
import { toast } from "sonner";

import { AiAsk } from "@/components/AiAsk";
export default function Dashboard() {
  const navigate = useNavigate();
  const { user, isPremium, checkAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [recs, setRecs] = useState([]);
  const [saved, setSaved] = useState([]);
  const [history, setHistory] = useState({ savings: [], hill: [] });

  useEffect(() => {
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/saved-vehicles").then((r) => setSaved(r.data.items)).catch(() => {});
    api.get("/analysis/history").then((r) => setHistory(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || isPremium()) return;
    const hasReturn = new URLSearchParams(window.location.search).get("doku_invoice");
    (async () => {
      try {
        const { data } = await api.post("/payments/doku/reconcile");
        if (data.activated) { api.post("/meta/track", { event: "payment_success" }).catch(() => {}); toast.success("Pembayaran berhasil! Premium Anda telah aktif."); await checkAuth(); }
        else if (hasReturn) toast.info("Pembayaran Anda sedang diproses. Premium akan aktif otomatis setelah pembayaran terkonfirmasi.");
      } catch { /* noop */ }
      finally { if (hasReturn) window.history.replaceState({}, "", "/dashboard"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const params = { category: "motor_listrik", sort: "price_asc" };
    if (profile?.budget) params.max_price = profile.budget;
    api.get("/vehicles", { params }).then((r) => setRecs(r.data.items.slice(0, 3))).catch(() => {});
  }, [profile]);

  const premiumActive = isPremium();
  const daysLeft = user?.premium_expires_at ? Math.ceil((new Date(user.premium_expires_at) - new Date()) / 86400000) : 0;

  const ACTIONS = [
    { icon: Calculator, title: "Hitung Penghematan", to: "/hitung", color: "bg-teal" },
    { icon: Mountain, title: "Cek Tanjakan", to: "/tanjakan", color: "bg-navy" },
    { icon: GitCompare, title: "Bandingkan Kendaraan", to: "/bandingkan", color: "bg-amber-500" },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="dashboard-page">
      {user && !user.email_verified && !["admin", "editor"].includes(user.role) && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3" data-testid="verify-email-banner">
          <MailWarning className="w-6 h-6 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-800 flex-1">Verifikasi email Anda untuk terus menggunakan alat analisis setelah percobaan pertama.</p>
          <Button size="sm" variant="outline" data-testid="resend-verify-btn" className="rounded-full" onClick={async () => { try { await api.post("/auth/resend-verification"); toast.success("Tautan verifikasi dikirim ke email Anda."); } catch { toast.error("Gagal mengirim."); } }}>Kirim Ulang</Button>
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-navy">Halo, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-muted-foreground mt-1">Selamat datang kembali. Mari cek kendaraan impian Anda.</p>
        </div>
        {premiumActive ? (
          <Card className="rounded-2xl px-5 py-3 flex items-center gap-3 bg-amber-50 border-amber-200" data-testid="dashboard-premium-status">
            <Crown className="w-6 h-6 text-amber-500" />
            <div><p className="text-xs text-amber-700">{user.subscription_plan === "3_day_pass" ? "Paket Coba · 3 Hari" : "Akses Premium"}</p><p className="font-heading font-bold text-navy">{user.role === "admin" ? "Tanpa batas" : user.subscription_plan === "3_day_pass" ? `Aktif sampai ${new Date(user.premium_expires_at).toLocaleString("id-ID", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" })} WIB` : `${daysLeft} hari tersisa`}</p></div>
          </Card>
        ) : (
          <Card className="rounded-2xl px-5 py-3 flex items-center gap-4 bg-white" data-testid="dashboard-trial-status">
            <div className="flex items-center gap-2"><Ticket className="w-6 h-6 text-teal" /><div><p className="text-xs text-muted-foreground">Analisis gratis</p><p className="font-heading font-bold text-navy text-lg">{user?.trial_credits ?? 0} tersisa</p></div></div>
            <Button size="sm" data-testid="dashboard-upgrade-btn" onClick={() => navigate("/premium")} className="rounded-full bg-teal hover:bg-teal-hover">Upgrade</Button>
          </Card>
        )}
      </div>

      <div className="mb-10"><AiAsk /></div>

      {/* Quick actions */}
      <div className="grid md:grid-cols-3 gap-4 mb-10">
        {ACTIONS.map((a) => (
          <button key={a.to} data-testid={`action-${a.to.slice(1)}`} onClick={() => navigate(a.to)}
            className="group bg-white rounded-2xl p-6 border border-border text-left hover:-translate-y-1 hover:shadow-lg transition-all flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl ${a.color} flex items-center justify-center`}><a.icon className="w-6 h-6 text-white" /></div>
            <div className="flex-1"><p className="font-heading font-bold text-navy">{a.title}</p></div>
            <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-teal group-hover:translate-x-1 transition-all" />
          </button>
        ))}
      </div>

      {/* Recommendations */}
      <section className="mb-10">
        <h2 className="font-heading font-bold text-2xl text-navy mb-4">Rekomendasi untuk Anda</h2>
        {profile?.budget ? <p className="text-sm text-muted-foreground mb-4">Berdasarkan anggaran {formatRupiah(profile.budget)} dari profil Anda.</p> : <p className="text-sm text-muted-foreground mb-4">Motor listrik paling terjangkau.</p>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {recs.map((v) => <VehicleCard key={v.vehicle_id} v={v} />)}
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Last results */}
        <section>
          <h2 className="font-heading font-bold text-2xl text-navy mb-4 flex items-center gap-2"><History className="w-6 h-6" /> Hasil Terakhir</h2>
          <div className="space-y-3">
            {history.savings.length === 0 && history.hill.length === 0 && (
              <Card className="rounded-2xl p-8 text-center text-muted-foreground" data-testid="empty-history">Belum ada hasil analisis. Mulai dengan Hitung Penghematan.</Card>
            )}
            {history.savings.slice(0, 3).map((h) => (
              <Card key={h.result_id} className="rounded-2xl p-4 flex items-center justify-between">
                <div><p className="text-xs text-muted-foreground">Penghematan</p><p className="font-semibold text-navy">{h.ev_name}</p><p className="text-teal font-bold">{formatRupiah(h.result?.monthly_savings)}/bulan</p></div>
                <TrafficLight status={h.result?.status} />
              </Card>
            ))}
            {history.hill.slice(0, 2).map((h) => (
              <Card key={h.result_id} className="rounded-2xl p-4 flex items-center justify-between">
                <div><p className="text-xs text-muted-foreground">Cek Tanjakan</p><p className="font-semibold text-navy">{h.vehicle_name}</p></div>
                <TrafficLight status={h.result?.status} />
              </Card>
            ))}
          </div>
        </section>

        {/* Saved */}
        <section>
          <h2 className="font-heading font-bold text-2xl text-navy mb-4 flex items-center gap-2"><Bookmark className="w-6 h-6" /> Kendaraan Tersimpan</h2>
          {saved.length === 0 ? (
            <Card className="rounded-2xl p-8 text-center text-muted-foreground" data-testid="empty-saved">Belum ada kendaraan tersimpan. Simpan dari halaman katalog.</Card>
          ) : (
            <div className="space-y-3">
              {saved.map((v) => (
                <Card key={v.vehicle_id} onClick={() => navigate(`/kendaraan/${v.vehicle_id}`)} className="rounded-2xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md">
                  {v.image && <img src={v.image} alt={v.model} className="w-16 h-16 rounded-xl object-cover" />}
                  <div><p className="text-xs text-muted-foreground">{v.brand}</p><p className="font-semibold text-navy">{v.model}</p><p className="text-teal font-bold text-sm">{formatRupiah(v.official_price)}</p></div>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
